import { PolicyTransaction, PolicyTransactionStatus, PolicyTransactionTypes } from '@models/policyTransaction';
import { setDateToMidnight } from '@utils/index';

// Notice/flag transaction types are unaffected by an out-of-sequence endorsement
// and are excluded server-side from both affected-transaction detection and the
// resolution snapshot (lio-portal-api OOSE_IGNORED_TRANSACTION_TYPES / design doc
// §2.5). They must not be surfaced as "blocking" here either, or the confirmation
// modal would list transactions the resolution never actually touches — and, for a
// notice-only case, would route the user into an OoSE flow the server treats as
// in-sequence (no resolution is created).
const OOSE_IGNORED_TRANSACTION_TYPES: string[] = [
  PolicyTransactionTypes.ConditionalRenewal,
  PolicyTransactionTypes.DoNotRenew,
  PolicyTransactionTypes.RescindDoNotRenew,
  PolicyTransactionTypes.DoNotReinstate,
  PolicyTransactionTypes.RescindDoNotReinstate,
  PolicyTransactionTypes.ManageRoles, // 'BrokerReassignment'
];

const isAllowedAfterDateTransaction = (transaction: PolicyTransaction) => {
  if (transaction.transactionType === PolicyTransactionTypes.Cancelation) {
    return (
      transaction.status === PolicyTransactionStatus.Processed ||
      transaction.status === PolicyTransactionStatus.RolledBack
    );
  }

  if (transaction.transactionType === PolicyTransactionTypes.Reinstatement) {
    return transaction.status === PolicyTransactionStatus.Processed;
  }

  return false;
};

export const getOutOfSequenceBlockingTransactions = ({
  policyTransactions,
  effectiveOnDate,
}: {
  policyTransactions: PolicyTransaction[];
  effectiveOnDate: Date;
}) => {
  const normalizedEffectiveOnDate = setDateToMidnight(new Date(effectiveOnDate));

  return policyTransactions
    .filter((transaction) => {
      if (transaction.status === PolicyTransactionStatus.Canceled) {
        return false;
      }

      // Excluded notice/flag types are never part of an OoSE resolution.
      if (OOSE_IGNORED_TRANSACTION_TYPES.includes(transaction.transactionType)) {
        return false;
      }

      const transactionEffectiveOnDate = setDateToMidnight(new Date(transaction.effectiveOn));
      if (transactionEffectiveOnDate <= normalizedEffectiveOnDate) {
        return false;
      }

      return !isAllowedAfterDateTransaction(transaction);
    })
    .sort((a, b) => {
      const effectiveDiff = new Date(a.effectiveOn).getTime() - new Date(b.effectiveOn).getTime();
      if (effectiveDiff !== 0) return effectiveDiff;
      // Tiebreak by processedOn (ascending) so transactions are shown in the
      // order they were processed when they share the same effective date.
      const aProcessed = a.processedOn ? new Date(a.processedOn).getTime() : 0;
      const bProcessed = b.processedOn ? new Date(b.processedOn).getTime() : 0;
      return aProcessed - bProcessed;
    });
};
